// @flow
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import {useForm, Controller} from 'react-hook-form';
import {getFilledValues} from '../../../../utils/form';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../assets/styles/colors';
import TelInput from '../../../../elements/TelInput/TelInput';

import type {User} from '../../../../entities';
import type {SaveAccountRequest} from '../../api';
import {REGEX} from '../../../../constants';

type Props = {|
  user: User,
  saveAccountSettings: (data: SaveAccountRequest) => void,
  isSaving?: boolean,
|};

const useStyles = makeStyles(theme => ({
  saveButton: {
    margin: 'auto',
    color: 'white',
    backgroundColor: colors.purple3,
  },
  center: {
    display: 'flex',
    alignItems: 'center',
  },
}));

export default function AccountInformation(props: Props) {
  const {user, saveAccountSettings, isSaving} = props;

  const classes = useStyles();

  const {handleSubmit, register, errors, control} = useForm();

  const onSubmit = value => {
    const data: SaveAccountRequest = getFilledValues(
      {
        firstName: user.firstName === value.firstName ? null : value.firstName,
        lastName: user.lastName === value.lastName ? null : value.lastName,
        email: user.email === value.email ? null : value.email,
        phone: user.phone === value.phone ? null : value.phone,
      },
      ['firstName', 'lastName', 'email', 'phone']
    );
    if (Object.keys(data).length) {
      saveAccountSettings({data});
    }
  };

  return (
    user && (
      <div>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} justify="center">
            <Grid item xs={12} sm={12} md={12}>
              <CustomTextField
                label="Nombre"
                fullWidth
                name="firstName"
                autoComplete="firstName"
                error={!!errors.firstName}
                helperText={errors.firstName ? errors.firstName.message : ''}
                inputRef={register({
                  required: 'Campo obligatorio',
                })}
                type="text"
                defaultValue={user.firstName}
                disabled={isSaving}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              <CustomTextField
                label="Apellido"
                fullWidth
                name="lastName"
                autoComplete="lastName"
                error={!!errors.lastName}
                helperText={errors.lastName ? errors.lastName.message : ''}
                inputRef={register({
                  required: 'Campo obligatorio',
                })}
                type="text"
                defaultValue={user.lastName}
                disabled={isSaving}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              <CustomTextField
                label="Correo electrónico"
                fullWidth
                name="email"
                autoComplete="email"
                error={!!errors.email}
                helperText={errors.email ? errors.email.message : ''}
                inputRef={register({
                  required: 'Campo obligatorio',
                  pattern: {
                    value: REGEX.email,
                    message: 'Formato de email inválido',
                  },
                })}
                defaultValue={user.email}
                disabled={isSaving}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              <Controller
                control={control}
                name="phone"
                defaultValue={user.phone}
                render={({onChange, value}) => (
                  <TelInput
                    label="Teléfono de contacto"
                    fullWidth
                    defaultValue={user.phone}
                    name="phone"
                    disabled={isSaving}
                    onChange={v => {
                      onChange(v);
                    }}
                  />
                )}
              />
            </Grid>
            {user && user.type === 'vendor-user' && (
              <Grid item xs={12} sm={10} md={10} className={classes.center}>
                <CustomButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  className={classes.saveButton}
                  disabled={isSaving}
                >
                  Guardar cambios
                </CustomButton>
              </Grid>
            )}
          </Grid>
        </form>
      </div>
    )
  );
}

AccountInformation.propTypes = {
  user: PropTypes.object.isRequired,
  saveAccountSettings: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};
